/**
 * useExperts — the React binding for Expertise Discovery + Overlap (sections
 * 22f / 21e). Both are PURE, client-side, awareness-only derivations over the
 * shared snapshot — no Bedrock call, so no chance of inventing people, and no
 * per-person score anywhere.
 *
 * - `find(topic)` derives starting-point experts for a topic.
 * - `overlaps` is the standing set of possible-overlap findings across
 *   workstreams (suggested collaborations), recomputed from the snapshot.
 */

import { useCallback, useMemo, useState } from 'react';
import { useContextSnapshot } from './useContextSnapshot';
import { discoverExperts } from './expertise';
import { detectOverlaps } from './overlap';
import type { ExpertiseResult, WorkstreamOverlap } from './types';

export interface UseExpertsResult {
  topic: string;
  setTopic: (topic: string) => void;
  result: ExpertiseResult | null;
  overlaps: WorkstreamOverlap[];
  workstreamCount: number;
  loading: boolean;
  error: string | null;
  find: (topic?: string) => void;
  clear: () => void;
}

export function useExperts(): UseExpertsResult {
  const { snapshot, refs, loading, error } = useContextSnapshot();
  const [topic, setTopic] = useState('');
  const [result, setResult] = useState<ExpertiseResult | null>(null);

  const overlaps = useMemo(
    () => (snapshot ? detectOverlaps(snapshot.workstreams, { limit: 30 }) : []),
    [snapshot],
  );

  const find = useCallback(
    (override?: string) => {
      const q = (override ?? topic).trim();
      if (override !== undefined) setTopic(override);
      if (!snapshot || !q) {
        setResult(null);
        return;
      }
      setResult(discoverExperts(snapshot, refs, q));
    },
    [snapshot, refs, topic],
  );

  const clear = useCallback(() => setResult(null), []);

  return {
    topic,
    setTopic,
    result,
    overlaps,
    workstreamCount: snapshot?.workstreams.length ?? 0,
    loading,
    error,
    find,
    clear,
  };
}
